import { PaziaLoader } from "components/atoms";
import { useCourseSchedulePreview } from "api/hooks";

interface CourseScheduleDurationPreviewProps {
  courseId: number;
}

const convertMinutesToHours = (minutes: number): number => {
  return Number((minutes / 60).toFixed(2));
};

const convertMinutesToLessonHours = (minutes: number): number => {
  return Number((minutes / 45).toFixed(2));
};

const formatHours = (hours: number): string => {
  if (hours === 1) return "1 godzina";
  if (hours >= 2 && hours <= 4) return `${hours} godziny`;
  return `${hours} godzin`;
};

export const CourseScheduleDurationPreview = ({ courseId }: CourseScheduleDurationPreviewProps) => {
  const { data, isLoading, error } = useCourseSchedulePreview(courseId);

  if (isLoading) return <PaziaLoader position="center" />;

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500 dark:text-slate-400">Nie udało się wczytać podglądu harmonogramu</div>
      </div>
    );
  }

  if (data.scheduleItems.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500 dark:text-slate-400">Brak elementów harmonogramu</div>
      </div>
    );
  }

  const totalHours = convertMinutesToHours(data.totalDuration);
  const totalLessonHours = convertMinutesToLessonHours(data.totalDuration);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {data.scheduleItems.map((item, index) => {
          const hours = convertMinutesToHours(item.duration);
          const lessonHours = convertMinutesToLessonHours(item.duration);

          return (
            <div
              key={index}
              className="flex items-center justify-between gap-4 border-b border-gray-200 pb-2 last:border-b-0 dark:border-slate-700"
            >
              <div className="flex min-w-0 flex-col">
                <span className="font-medium text-gray-900 dark:text-slate-100">{item.name}</span>
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  {item.timeFrom} - {item.timeTo}
                </span>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm font-medium text-gray-700 dark:text-slate-300">{formatHours(hours)}</div>
                <div className="whitespace-nowrap text-xs text-gray-500 dark:text-slate-400">
                  {lessonHours} h lekcyjnych
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-2 border-t-2 border-gray-200 pt-4 dark:border-slate-600">
        <div className="flex justify-between">
          <span className="font-semibold text-gray-900 dark:text-slate-100">Łączny czas trwania:</span>
          <span className="font-semibold text-gray-900 dark:text-slate-100">{formatHours(totalHours)}</span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold text-gray-900 dark:text-slate-100">Godziny lekcyjne:</span>
          <span className="font-semibold text-gray-900 dark:text-slate-100">{totalLessonHours} h</span>
        </div>
      </div>
    </div>
  );
};
